import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  onSnapshot,
  query,
  where,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/firebase";

import Sidebar from "../components/layout/Sidebar";
import Topbar from "../components/layout/Topbar";

import "./Assinaturas.css";
import "./BaseLayout.css";

export default function Assinaturas() {
  const [loading, setLoading] = useState(true);
  const [usersPaid, setUsersPaid] = useState([]);
  const [search, setSearch] = useState("");

  // ===============================
  // Carrega usuários Plus/Premium/Diamond
  // ===============================
  useEffect(() => {
    setLoading(true);

    // 🔥 MVP seguro: pega só quem tem plano pago
    // (evita query do tipo "!=" que costuma exigir índices/ordenação)
    const q = query(
      collection(db, "users"),
      where("plan", "in", ["plus", "premium", "diamond"])
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setUsersPaid(list);
        setLoading(false);
      },
      (err) => {
        console.error("Erro ao carregar assinaturas:", err);
        setUsersPaid([]);
        setLoading(false);
      }
    );

    return () => unsub();
  }, []);

  // ===============================
  // Helpers
  // ===============================
  const now = useMemo(() => new Date(), [usersPaid]); // recalcula quando lista muda
  const plus3Days = useMemo(
    () => new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000),
    [now]
  );

  const toDateSafe = (value) => {
    // Firestore Timestamp => value.toDate()
    // Date => value
    // string/number => new Date(...)
    if (!value) return null;
    try {
      if (typeof value?.toDate === "function") return value.toDate();
      if (value instanceof Date) return value;
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? null : d;
    } catch {
      return null;
    }
  };

  const formatDateBR = (d) => {
    if (!d) return "-";
    return d.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getPlanLabel = (plan) => {
    const p = String(plan || "").toLowerCase().trim();
    if (p === "diamond") return "Diamond";
    if (p === "premium") return "Premium";
    if (p === "plus") return "Plus";
    return "Free";
  };

  const daysDiffCeil = (a, b) => {
    // diferença em dias (arredonda pra cima)
    const ms = b.getTime() - a.getTime();
    return Math.ceil(ms / (24 * 60 * 60 * 1000));
  };

  // ===============================
  // Monta listas (expira em 3 dias / expirados)
  // - Prioriza planExpiresAt (novo)
  // - Fallback para planoExpiraEm (legado)
  // ===============================
  const normalized = useMemo(() => {
    return usersPaid
      .map((u) => {
        const expiresAt =
          toDateSafe(u.planExpiresAt) || toDateSafe(u.planoExpiraEm);
        return {
          ...u,
          _expiresAtDate: expiresAt,
        };
      })
      .filter((u) => !!u._expiresAtDate); // só quem tem expiração definida
  }, [usersPaid]);

  const expiresSoon = useMemo(() => {
    return normalized
      .filter((u) => u._expiresAtDate >= now && u._expiresAtDate <= plus3Days)
      .sort((a, b) => a._expiresAtDate - b._expiresAtDate);
  }, [normalized, now, plus3Days]);

  const expired = useMemo(() => {
    return normalized
      .filter((u) => u._expiresAtDate < now)
      .sort((a, b) => b._expiresAtDate - a._expiresAtDate);
  }, [normalized, now]);

  // ===============================
  // Busca (nome/email/id)
  // ===============================
  const applySearch = (list) => {
    const s = search.trim().toLowerCase();
    if (!s) return list;

    return list.filter((u) => {
      const name = String(u.name || u.nome || "").toLowerCase();
      const email = String(u.email || "").toLowerCase();
      const id = String(u.id || "").toLowerCase();
      return name.includes(s) || email.includes(s) || id.includes(s);
    });
  };

  const expiresSoonFiltered = useMemo(
    () => applySearch(expiresSoon),
    [expiresSoon, search]
  );
  const expiredFiltered = useMemo(
    () => applySearch(expired),
    [expired, search]
  );

  // ===============================
  // Ações (MVP)
  // - Mantém campos novos e também sincroniza legado quando fizer sentido
  // ===============================
  const setFree = async (userId) => {
    const ok = window.confirm(
      "Tem certeza que deseja rebaixar este usuário para FREE?"
    );
    if (!ok) return;

    try {
      await updateDoc(doc(db, "users", userId), {
        // novo padrão
        plan: "free",
        planExpiresAt: null,

        // legado (pra não ficar divergente)
        planoAtual: null,
        planoNome: "Free",
        planoExpiraEm: null,
        planoAtivo: false,

        lastPaymentStatus: "expired",
        updatedAt: serverTimestamp(),
      });
      alert("Usuário rebaixado para FREE.");
    } catch (e) {
      console.error(e);
      alert("Erro ao rebaixar usuário. Veja o console.");
    }
  };

  const extend30Days = async (userId, currentExpiresAtDate) => {
    const ok = window.confirm("Adicionar +30 dias para este usuário?");
    if (!ok) return;

    try {
      const base =
        currentExpiresAtDate && currentExpiresAtDate > now
          ? currentExpiresAtDate
          : now;

      const newDate = new Date(base.getTime() + 30 * 24 * 60 * 60 * 1000);

      await updateDoc(doc(db, "users", userId), {
        // novo padrão
        planExpiresAt: newDate,

        // legado
        planoExpiraEm: newDate,
        planoAtivo: true,

        lastPaymentStatus: "manual_paid",
        updatedAt: serverTimestamp(),
      });

      alert("+30 dias aplicado com sucesso.");
    } catch (e) {
      console.error(e);
      alert("Erro ao extender plano. Veja o console.");
    }
  };

  return (
    <div className="page-container">
      <Sidebar />

      <div className="page-content">
        <Topbar />

        <div className="subs-page">
          <div className="subs-header">
            <div className="subs-title">
              <h2>Assinaturas</h2>
              <p>
                Controle rápido do MVP: quem expira em 3 dias e quem já expirou.
              </p>
            </div>

            <div className="subs-search">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nome, e-mail ou ID..."
              />
            </div>
          </div>

          <div className="subs-stats">
            <div className="subs-stat-card">
              <span className="subs-stat-label">Expira em 3 dias</span>
              <span className="subs-stat-value">{expiresSoon.length}</span>
            </div>

            <div className="subs-stat-card">
              <span className="subs-stat-label">Expirados</span>
              <span className="subs-stat-value">{expired.length}</span>
            </div>
          </div>

          {loading ? (
            <div className="subs-loading">Carregando assinaturas...</div>
          ) : (
            <>
              {/* =========================
                  BLOCO: Expira em 3 dias
              ========================== */}
              <section className="subs-block">
                <div className="subs-block-header">
                  <h3>Expira em 3 dias</h3>
                  <span className="subs-block-hint">
                    Entre hoje e {formatDateBR(plus3Days)}
                  </span>
                </div>

                {expiresSoonFiltered.length === 0 ? (
                  <div className="subs-empty">
                    Nenhum usuário expirando nos próximos 3 dias.
                  </div>
                ) : (
                  <div className="subs-grid">
                    {expiresSoonFiltered.map((u) => {
                      const exp = u._expiresAtDate;
                      const daysLeft = daysDiffCeil(now, exp);

                      return (
                        <div className="subs-user-card" key={u.id}>
                          <div className="subs-user-top">
                            <div className="subs-user-main">
                              <div className="subs-user-name">
                                {u.name || u.nome || "Sem nome"}
                              </div>
                              <div className="subs-user-email">{u.email || "-"}</div>
                              <div className="subs-user-meta">
                                <span className="pill plan">
                                  Plano: {getPlanLabel(u.plan)}
                                </span>
                                <span className="pill warn">
                                  Expira em {daysLeft} dia(s)
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="subs-user-bottom">
                            <div className="subs-user-dates">
                              <div>
                                <span className="label">Expira em</span>
                                <span className="value">{formatDateBR(exp)}</span>
                              </div>
                              <div>
                                <span className="label">User ID</span>
                                <span className="value mono">{u.id}</span>
                              </div>
                            </div>

                            <div className="subs-actions">
                              <button
                                className="btn-outline"
                                onClick={() => extend30Days(u.id, exp)}
                              >
                                +30 dias
                              </button>
                              <button className="btn-danger" onClick={() => setFree(u.id)}>
                                Rebaixar FREE
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>

              {/* =========================
                  BLOCO: Expirados
              ========================== */}
              <section className="subs-block">
                <div className="subs-block-header">
                  <h3>Expirados</h3>
                  <span className="subs-block-hint">
                    Já passaram da data de expiração
                  </span>
                </div>

                {expiredFiltered.length === 0 ? (
                  <div className="subs-empty">Nenhum usuário expirado.</div>
                ) : (
                  <div className="subs-grid">
                    {expiredFiltered.map((u) => {
                      const exp = u._expiresAtDate;
                      const daysAgo = Math.abs(daysDiffCeil(exp, now));

                      return (
                        <div className="subs-user-card expired" key={u.id}>
                          <div className="subs-user-top">
                            <div className="subs-user-main">
                              <div className="subs-user-name">
                                {u.name || u.nome || "Sem nome"}
                              </div>
                              <div className="subs-user-email">{u.email || "-"}</div>
                              <div className="subs-user-meta">
                                <span className="pill plan">
                                  Plano: {getPlanLabel(u.plan)}
                                </span>
                                <span className="pill danger">
                                  Expirou há {daysAgo} dia(s)
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="subs-user-bottom">
                            <div className="subs-user-dates">
                              <div>
                                <span className="label">Expirou em</span>
                                <span className="value">{formatDateBR(exp)}</span>
                              </div>
                              <div>
                                <span className="label">User ID</span>
                                <span className="value mono">{u.id}</span>
                              </div>
                            </div>

                            <div className="subs-actions">
                              <button
                                className="btn-outline"
                                onClick={() => extend30Days(u.id, exp)}
                              >
                                Renovar +30 dias
                              </button>
                              <button className="btn-danger" onClick={() => setFree(u.id)}>
                                Rebaixar FREE
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
